import React, { useState, useCallback } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import styles from "./BatchProcessing.module.css";

const BatchProcessing = () => {
  const [images, setImages] = useState([]);
  const [convertedImages, setConvertedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(80);
  const [outputFormat, setOutputFormat] = useState("jpg");
  const [batchProgress, setBatchProgress] = useState({});

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length) {
      processImages(files);
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) {
      processImages(files);
    }
  };

  const processImages = (files) => {
    setError(null);

    const imageFiles = files.filter((file) => file.type.match("image.*"));
    if (imageFiles.length === 0) {
      setError("Please upload image files");
      return;
    }

    const processedImages = [];
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const newImage = {
            file,
            url: event.target.result,
            name: file.name,
            type: file.type,
            size: file.size,
            dimensions: { width: img.width, height: img.height },
          };
          processedImages.push(newImage);

          if (processedImages.length === imageFiles.length) {
            setImages(processedImages);
            setConvertedImages([]);
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const convertImage = (image) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        let mimeType, fileExtension;
        switch (outputFormat) {
          case "jpg":
            mimeType = "image/jpeg";
            fileExtension = "jpg";
            break;
          case "png":
            mimeType = "image/png";
            fileExtension = "png";
            break;
          case "webp":
            mimeType = "image/webp";
            fileExtension = "webp";
            break;
          default:
            mimeType = "image/jpeg";
            fileExtension = "jpg";
        }

        const convertedUrl = canvas.toDataURL(
          mimeType,
          outputFormat === "jpg" ? quality / 100 : undefined
        );

        const base64String = convertedUrl.split(",")[1];
        const fileSize = base64String
          ? Math.round((base64String.length * 3) / 4)
          : 0;

        resolve({
          original: image,
          url: convertedUrl,
          name: `${image.name.split(".")[0]}.${fileExtension}`,
          size: fileSize,
          dimensions: { width: img.width, height: img.height },
          quality: quality,
          format: outputFormat.toUpperCase(),
        });
      };
      img.src = image.url;
    });
  };

  const convertBatch = async () => {
    if (images.length === 0) return;

    setBatchProgress({});
    const results = [];

    for (let i = 0; i < images.length; i++) {
      setBatchProgress((prev) => ({
        ...prev,
        [images[i].name]: `Processing ${i + 1}/${images.length}`,
      }));

      const result = await convertImage(images[i]);
      results.push(result);

      setBatchProgress((prev) => ({
        ...prev,
        [images[i].name]: `Completed ${i + 1}/${images.length}`,
      }));
    }

    setConvertedImages(results);
  };

  const downloadAllAsZip = async () => {
    const zip = new JSZip();

    convertedImages.forEach((convertedImage) => {
      const byteString = atob(convertedImage.url.split(",")[1]);
      const mimeType = convertedImage.url
        .split(",")[0]
        .split(":")[1]
        .split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeType });

      zip.file(convertedImage.name, blob);
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `batch-converted-${outputFormat}.zip`);
  };

  const resetAll = () => {
    setImages([]);
    setConvertedImages([]);
    setError(null);
    setBatchProgress({});
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 bytes";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const totalOriginalSize = images.reduce((sum, img) => sum + img.size, 0);
  const totalConvertedSize = convertedImages.reduce(
    (sum, img) => sum + img.size,
    0
  );
  const totalSavings = totalOriginalSize - totalConvertedSize;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Batch Image Processing</h1>

      {/* Step Guide */}
      {images.length === 0 && (
        <div className={styles.stepsGuide}>
          <h2>Convert Multiple Images at Once</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4>Upload Multiple Images</h4>
                <p>Select or drag & drop multiple image files</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4>Choose Settings</h4>
                <p>Select output format and quality settings</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4>Convert & Download</h4>
                <p>Process all images and download as ZIP file</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {images.length === 0 && (
        <div className={styles.uploadSection}>
          <div
            className={`${styles.dropArea} ${
              isDragging ? styles.dragOver : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drop your images here</h3>
              <p className={styles.dropText}>or</p>
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
                multiple
              />
              <button className={styles.browseButton}>
                Select Multiple Images
              </button>
              <p className={styles.supportedFormats}>
                Supports JPG, PNG, WEBP, GIF, BMP - No limit on number of files
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Content Section */}
      {images.length === 0 && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Business-Grade Batch Processing Section */}
            <div className={styles.contentBlock}>
              <h2>Business-Grade Batch Image Processing</h2>
              <p>
                Revolutionize how you work with images by taking advantage of
                business-grade batch-processing capabilities. You can process
                thousands of images at once without losing quality and achieving
                efficiencies that professions like businesses, photographers,
                and content creators require.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🏢</div>
                  <h3>Unlimited Scalability</h3>
                  <p>
                    Process hundreds or thousands of images in a single batch
                    without any artificial limit.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>⚡</div>
                  <h3>Process in Parallel</h3>
                  <p>
                    You can convert multiple images at the same time to greatly
                    shorten processing time.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📦</div>
                  <h3>Smart Packaging</h3>
                  <p>
                    Automatically package images after conversion into organized
                    ZIP files and recreate consistent naming in so doing.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🔧</div>
                  <h3>Workflow Integration</h3>
                  <p>
                    Seamlessly integrate into your own systems and receive
                    consistent, reliable performance.
                  </p>
                </div>
              </div>
            </div>

            {/* Industry Workflows */}
            <div className={styles.contentBlock}>
              <h2>Optimized Workflows In Your Industry</h2>
              <div className={styles.workflowGrid}>
                <div className={styles.workflowItem}>
                  <h4>📸 Photographer</h4>
                  <p>
                    You can process an entire gallery or database of images from
                    RAW to a usable web format at the same time, while
                    maintaining consistent color grading, sizing, and other
                    aspects of conversion.
                  </p>
                  <div className={styles.workflowStats}>
                    <span>Typical Batch: 500-2000 images</span>
                    <span>Time Saving: 4-8 hours</span>
                  </div>
                </div>
                <div className={styles.workflowItem}>
                  <h4>🛒 E-commerce</h4>
                  <p>
                    You can take product catalog images and convert them into
                    optimized formats for the web, while maintaining visual
                    consistency amongst the other product images.
                  </p>
                  <div className={styles.workflowStats}>
                    <span>Typical Batch: 100-1000 images</span>
                    <span>Time Saving: 2-6 hours</span>
                  </div>
                </div>
                <div className={styles.workflowItem}>
                  <h4>📰 Media</h4>
                  <p>
                    You can process editorial images with a consistent
                    compression policy to allow for improved page load times and
                    SEO.
                  </p>
                  <div className={styles.workflowStats}>
                    <span>Typical Batch: 50-500 images</span>
                    <span>Time Saving: 1-3 hours</span>
                  </div>
                </div>
                <div className={styles.workflowItem}>
                  <h4>🎓 Education</h4>
                  <p>
                    You can optimize course materials and images used in
                    documentation for digital learning programs.
                  </p>
                  <div className={styles.workflowStats}>
                    <span>Typical Batch: 200-1000 images</span>
                    <span>Time Saving: 3-5 hours</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Architecture */}
            <div className={styles.contentBlock}>
              <h2>Advanced Processing Architecture</h2>
              <div className={styles.architecture}>
                <div className={styles.architectureStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <h4>Smart Queue Management</h4>
                    <p>
                      Automatically manages the image queues in order to reduce
                      queues and maximize queues and performance.
                    </p>
                  </div>
                </div>
                <div className={styles.architectureStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <h4>Memory-Optimized Processing</h4>
                    <p>
                      Persistent handling of large batches without browser
                      crashes.
                    </p>
                  </div>
                </div>
                <div className={styles.architectureStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <h4>Progressive Compression</h4>
                    <p>
                      Maintains visibility while drastically reducing files.
                    </p>
                  </div>
                </div>
                <div className={styles.architectureStep}>
                  <div className={styles.stepNumber}>4</div>
                  <div className={styles.stepContent}>
                    <h4>Smart ZIP Packaging</h4>
                    <p>Creates structured archives for distribution.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Format Conversion Strategies */}
            <div className={styles.contentBlock}>
              <h2>Strategic Convert Formats</h2>
              <div className={styles.conversionStrategies}>
                <div className={styles.strategy}>
                  <h4>🔄 Legacy to Modern Conversion</h4>
                  <p>
                    <strong>Scenario:</strong> Upgrading old collections into
                    new formats
                    <br />
                    <strong>Process:</strong> BMP or TIFF swap to WebP or JPG
                    <br />
                    <strong>Benefits:</strong> 60% to 80% smaller files
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>🌐 Multi-Platform Background Preparation</h4>
                  <p>
                    <strong>Scenario:</strong> Preparing assets for the web,
                    mobile, and print
                    <br />
                    <strong>Process:</strong> Producing the web with webP and
                    printing with jpg
                    <br />
                    <strong>Benefits:</strong> Only one session for all
                    conversions
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>📱 Responsive Image Creation</h4>
                  <p>
                    <strong>Scenario:</strong> Need for responsive web images
                    <br />
                    <strong>Process:</strong> Producing at any and all sizes or
                    formats at the same time
                    <br />
                    <strong>Benefit:</strong> All responsive sets in one instant
                  </p>
                </div>
                <div className={styles.strategy}>
                  <h4>🏢 Enterprise Migration</h4>
                  <p>
                    <strong>Scenario:</strong> migrating a large collection of
                    images
                    <br />
                    <strong>Process:</strong> Large batch of images converted
                    with quality
                    <br />
                    <strong>Benefit:</strong> All formats are well entact
                  </p>
                </div>
              </div>
            </div>

            {/* Quality Assurance */}
            <div className={styles.contentBlock}>
              <h2>Enterprise Quality Assurance</h2>
              <div className={styles.qualityFeatures}>
                <div className={styles.qualityFeature}>
                  <h4>🔍 Consistent Output Quality</h4>
                  <p>
                    Same processing configurations enable visual consistency.
                  </p>
                </div>
                <div className={styles.qualityFeature}>
                  <h4>📊 Batch Validation</h4>
                  <p>
                    Automatically detects and reports any conversions that have
                    failed.
                  </p>
                </div>
                <div className={styles.qualityFeature}>
                  <h4>🔄 Format Integrity</h4>
                  <p>
                    Maintains color profiles, metadata, and visual accuracy.
                  </p>
                </div>
                <div className={styles.qualityFeature}>
                  <h4>📈 Performance Metrics</h4>
                  <p>Reports on timing, size reductions, and quality.</p>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div className={styles.contentBlock}>
              <h2>Enterprise Best Practices</h2>
              <div className={styles.bestPractices}>
                <div className={styles.practice}>
                  <h4>📁 Organize by Project</h4>
                  <p>Consolidate your images to ensure consistent results.</p>
                </div>
                <div className={styles.practice}>
                  <h4>⚙️ Test with Samples</h4>
                  <p>
                    It is best practice to run tests on 5–10 images before
                    processing a large batch.
                  </p>
                </div>
                <div className={styles.practice}>
                  <h4>🔧 Use Appropriate Formats</h4>
                  <p>Use WEBP for web, JPG for photos, and PNG for graphics.</p>
                </div>
                <div className={styles.practice}>
                  <h4>📋 Maintain Originals</h4>
                  <p>
                    Create backup copies of the source files before processing.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className={styles.contentBlock}>
              <h2>Frequently Asked Questions</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>Is there a limit to how many images I can process?</h4>
                  <p>
                    There is no hard limit. However, very large batches (1000+
                    images) will take longer, and there is limit after 4000
                    images to maintain system performance.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>How does it handle different image types?</h4>
                  <p>
                    The processing detects and converts mixed formats
                    consistently (jpg, png or webp navigating image types).
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I pause and resume batch processing?</h4>
                  <p>
                    There is no option to pause and resume (yet) However, you
                    can break a large batch into smaller groups for stability.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What if my internet drops?</h4>
                  <p>
                    The processing exists locally in the browser environment,
                    all images that have been completed and exist in the app
                    remain intact.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Is there a file size limitation?</h4>
                  <p>
                    Not hard limit, however images that are very large (50MB +)
                    may take longer.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>
                    Can I customize output settings for different image types?
                  </h4>
                  <p>
                    Currently, settings apply uniformly to the entire batch. For
                    mixed content with different requirements, we recommend
                    processing similar image types in separate batches for
                    optimal results.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Main Content */}
      {images.length > 0 && (
        <div className={styles.editorSection}>
          <div className={styles.mainContent}>
            {/* Left Side - Controls */}
            <div className={styles.controlsSide}>
              <div className={styles.sectionHeader}>
                <h3>Uploaded Images ({images.length})</h3>
                <button onClick={resetAll} className={styles.changeImageButton}>
                  Clear All
                </button>
              </div>

              {/* Format Selection */}
              <div className={styles.formatSelector}>
                <label>Output Format:</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  className={styles.formatSelect}
                >
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>

              {/* Quality Control */}
              {outputFormat === "jpg" && (
                <div className={styles.qualityControl}>
                  <label htmlFor="qualitySlider">Quality: {quality}%</label>
                  <input
                    type="range"
                    id="qualitySlider"
                    min="10"
                    max="100"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    className={styles.qualitySlider}
                  />
                </div>
              )}

              {/* Convert Button */}
              <div className={styles.buttonGroup}>
                <button
                  onClick={convertBatch}
                  className={styles.processButton}
                  disabled={convertedImages.length > 0}
                >
                  {convertedImages.length > 0
                    ? "Convert Again"
                    : `Convert All to ${outputFormat.toUpperCase()}`}
                </button>
              </div>

              {/* Progress */}
              {Object.keys(batchProgress).length > 0 && (
                <div className={styles.progressSection}>
                  <h4>Conversion Progress</h4>
                  {images.map((image, index) => (
                    <div key={index} className={styles.progressItem}>
                      <span className={styles.fileName}>{image.name}</span>
                      <span className={styles.progressStatus}>
                        {batchProgress[image.name] || "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Results */}
            {convertedImages.length > 0 && (
              <div className={styles.resultsSide}>
                <div className={styles.resultsSection}>
                  <h3>Conversion Results</h3>

                  {/* Summary */}
                  <div className={styles.batchSummary}>
                    <h4>Batch Summary</h4>
                    <div className={styles.summaryItem}>
                      <span>Total Files:</span>
                      <strong>{convertedImages.length}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Original Size:</span>
                      <strong>{formatFileSize(totalOriginalSize)}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Converted Size:</span>
                      <strong>{formatFileSize(totalConvertedSize)}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Space Saved:</span>
                      <strong className={styles.savings}>
                        {formatFileSize(totalSavings)} (
                        {Math.round((totalSavings / totalOriginalSize) * 100)}%)
                      </strong>
                    </div>
                  </div>

                  {/* Download Button */}
                  <div className={styles.downloadSection}>
                    <button
                      onClick={downloadAllAsZip}
                      className={styles.downloadButton}
                    >
                      📥 Download All as ZIP
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchProcessing;
